import React, {
	useMemo,
	useState,
} from 'react';
import {
	Alert,
	Col,
	Divider,
	Row,
	Space,
	Typography,
} from 'antd';
import { Moment } from 'moment';
import { useHistory } from 'react-router';
import { useQuery } from 'react-query';
import {
	AccrualItemClosingPeriodFormats,
	AccrualItemStates,
	AccrualItemTypes,
	ActualAccrualFields,
	DATE_AND_TIME,
	FixtureTypeLabels,
} from '@shared/utils/constants';
import { formatCurrency } from '@shared/utils/currency';
import { toMoment } from '@shared/utils/date';
import type { TransformedAccrualItem } from '@api/features/financials/getAccrualItems';
import { formatDate } from '@client/utils/formatDate';
import SimpleScreen from '@client/components/screens/SimpleScreen';
import { Links } from '@client/utils/links';
import Card from '@client/components/Card/Card';
import Select from '@client/components/Select';
import useFetchedState from '@client/utils/hooks/useFetchedState';
import {
	closeAccountingItems,
	getAccrualItems,
	getPreviousCloses,
	getVessels,
	getVoyageDurations,
	getVoyages,
} from '@client/lib/api';
import { useAuth } from '@client/lib/auth';
import Button from '@client/components/Button';
import showErrorNotification from '@client/utils/showErrorNotification';
import showSuccessNotification from '@client/utils/showSuccessNotification';
import DatePicker from '@client/components/DatePicker';
import Table from '@client/components/Table/Table';
import styles from './PeriodCloseScreen.module.css';

const PeriodCloseScreen = () => {
	const { userInfo } = useAuth();
	const [closePeriod, setClosePeriod] = useState<Moment | null>();

	const [selectedVessels, setSelectedVessels] = useState<number[]>([]);
	const [selectedVoyages, setSelectedVoyages] = useState<number[]>([]);

	const [vessels] = useFetchedState(getVessels);

	const vesselKey = Array.isArray(selectedVessels) ?
		selectedVessels :
		[selectedVessels];

	const { data: voyages } = useQuery(
		['voyages', vesselKey],
		() =>
			getVoyages({
				vesselId: vesselKey,
				disablePagination: true,
				includeCompleted: true,
			}),
		{
			keepPreviousData: true,
			refetchOnWindowFocus: false,
		},
	);

	const [accrualItems, _refresh, _error, loading] = useFetchedState(getAccrualItems);
	const [previousCloses] = useFetchedState(getPreviousCloses);
	const [contractDurations] = useFetchedState(
		async () => {
			if (selectedVoyages == null || selectedVoyages.length === 0) {
				return null;
			}

			return getVoyageDurations(selectedVoyages);
		},
		[selectedVoyages],
	);

	const eligibleItems = useMemo(() => {
		if (accrualItems == null) {
			return [];
		}

		const cutoff = userInfo.accountingCutoffDate;

		let items = accrualItems.filter((item) => {
			if (
				item.status !== AccrualItemStates.OPEN ||
				(item.item !== ActualAccrualFields.ACTUAL_TC_HIRE &&
					item.item !== ActualAccrualFields.ACTUAL_BB_TC_HIRE)
			) {
				return false;
			}

			if (
				cutoff != null &&
				!toMoment(item.invoiceDate ?? item.posted).isAfter(toMoment(cutoff))
			) {
				return false;
			}

			const remaining = (item.totalAmount ?? item.amount) - (item.recognisedToDate ?? 0);

			if (remaining <= 0.01) {
				return false;
			}

			return true;
		});

		if (selectedVessels.length > 0 && selectedVessels[0] !== -1) {
			items = items.filter((item) => selectedVessels.includes(item.vessel.id));
		}

		if (selectedVoyages.length > 0 && selectedVoyages[0] !== -1) {
			items = items.filter((item) => selectedVoyages.includes(item.voyage.id));
		}

		return items;
	}, [accrualItems, selectedVessels, selectedVoyages, userInfo.accountingCutoffDate]);

	const isPeriodAlreadyClosedForSelection = useMemo(() => {
		if (closePeriod == null || previousCloses == null) {
			return false;
		}

		const periodKey = closePeriod.format('YYYY-MM');

		const isAllVessels =
			selectedVessels.length === 0 ||
			(selectedVessels.length > 0 && selectedVessels[0] === -1) ||
			selectedVessels.includes(-1);

		const vesselIdsToClose = isAllVessels ?
			[...new Set(eligibleItems.map((i) => i.vessel.id))] :
			selectedVessels.filter((id) => id !== -1);

		if (vesselIdsToClose.length === 0) {
			return false;
		}

		return vesselIdsToClose.every((vesselId) => (
			previousCloses.some((pc) => pc.period === periodKey && pc.vessel.id === vesselId)
		));
	}, [closePeriod, previousCloses, selectedVessels, eligibleItems]);

	const [previewItems] = useFetchedState(async () => {
		if (
			eligibleItems.length !== 0 &&
			closePeriod != null
		) {
			try {
				return await closeAccountingItems(
					eligibleItems,
					closePeriod,
					AccrualItemClosingPeriodFormats.MONTH_AND_YEAR,
					true,
				);
			} catch (e) {
				showErrorNotification('Something went wrong', e as Error);
			}
		}

		return null;
	}, [closePeriod, eligibleItems]);

	const history = useHistory();

	const onSubmit = async () => {
		if (
			eligibleItems.length !== 0 &&
			closePeriod != null
		) {
			try {
				await closeAccountingItems(
					eligibleItems,
					closePeriod,
					AccrualItemClosingPeriodFormats.MONTH_AND_YEAR,
				);
				showSuccessNotification(
					'Period closed successfully',
					`${closePeriod.format('yyyy-MMM')} has been closed`,
					5,
				);
				history.push(Links.Accounting.Accruals.get());
			} catch (e) {
				showErrorNotification('Something went wrong', e as Error);
			}
		} else {
			showErrorNotification('Could not close period since no eligible items were found');
		}
	};

	const journalColumns = [
		{
			title: 'Item Type',
			dataIndex: 'item',
			key: 'item',
			render: (v: string) => v,
		},
		{
			title: 'Description',
			key: 'description',
			render: (_v: unknown, row: TransformedAccrualItem) => row.origin ?? row.item,
		},
		{
			title: 'Posting Date',
			dataIndex: 'posted',
			key: 'posted',
			render: (v: string) => formatDate(toMoment(v).startOf('month')),
		},
		{
			title: 'Invoice Date',
			dataIndex: 'invoiceDate',
			key: 'invoiceDate',
			render: (v: string | null | undefined) => (v != null ? formatDate(v) : '-'),
		},
		{
			title: 'Code',
			dataIndex: 'accountCode',
			key: 'accountCode',
			render: (v: string | undefined) => v ?? <i>Unmapped</i>,
		},
		{
			title: 'Debit',
			key: 'debit',
			render: (_v: unknown, row: TransformedAccrualItem) => {
				const isDeferred = row.item.startsWith('Deferred');
				const amount = Math.abs(row.amount);

				if (row.type === AccrualItemTypes.ENTRY && isDeferred) {
					return formatCurrency(amount, row.currency);
				}

				if (row.type === AccrualItemTypes.REVERSAL && !isDeferred) {
					return formatCurrency(amount, row.currency);
				}

				return '-';
			},
		},
		{
			title: 'Credit',
			key: 'credit',
			render: (_v: unknown, row: TransformedAccrualItem) => {
				const isDeferred = row.item.startsWith('Deferred');
				const amount = Math.abs(row.amount);

				if (row.type === AccrualItemTypes.ENTRY && !isDeferred) {
					return formatCurrency(amount, row.currency);
				}

				if (row.type === AccrualItemTypes.REVERSAL && isDeferred) {
					return formatCurrency(amount, row.currency);
				}

				return '-';
			},
		},
	];

	const hasUnmappedAccounts = previewItems?.some((i) => !i.accountCode);

	return (
		<SimpleScreen
			title="Month Close"
			rootPageTitle="Accruals"
			canGoBack
			breadcrumbs={[['Accruals', Links.Accounting.Accruals.get()]]}
			goBackToUrl={Links.Accounting.Accruals.get()}
		>
			<Card>
				<Row>
					<Col span={6} className={styles.headerCol}>
						<Space size="middle">
							<Typography.Text>1.</Typography.Text>
							<Typography.Text>Select the month to close</Typography.Text>
						</Space>
					</Col>
					<Col className={styles.selectCol}>
						<Space direction="vertical">
							<b>Month & Year</b>
							<DatePicker
								picker="month"
								onChange={(date) => setClosePeriod(date as Moment)}
							/>
						</Space>
					</Col>
				</Row>
				<>
					<Divider />
					<Row>
						<Col span={6} className={styles.headerCol}>
							<Space size="middle">
								<Typography.Text>2.</Typography.Text>
								<Typography.Text>Select vessel(s) and contract(s)</Typography.Text>
							</Space>
						</Col>
						<Col className={styles.selectCol}>
							<Space size="middle">
								<Space direction="vertical">
									<b>Vessel</b>
									<Select
										mode="multiple"
										placeholder="Select a vessel"
										options={
											[
												{ label: 'All vessels', value: -1 },
												...(vessels ?? [])
													.sort((a, b) => a.name.localeCompare(b.name))
													.map((v) => ({
														label: v.name,
														value: v.id,
													})),
											]
										}
										onChange={(v) => {
											setSelectedVessels(v as number[]);
											setSelectedVoyages([]);
										}}
										className={styles.selector}
										notFoundContent="No vessels found"
										showSearch
									/>
								</Space>
								<Space direction="vertical">
									<b>Contract(s)</b>
									<Select
										value={selectedVoyages}
										placeholder="Select one or more contracts"
										mode="multiple"
										options={[
											...(selectedVessels.length === 0 ? [] : [{ label: 'All contracts', value: -1 }]),
											...(voyages?.rows ?? [])
												.sort((a, b) => a.identifier.localeCompare(b.identifier))
												.map((v) => ({
													label: v.identifier,
													value: v.id,
												})),
										]}
										onChange={(v) => setSelectedVoyages(v as number[])}
										className={styles.multipleSelector}
										notFoundContent="Selected vessel has no contracts"
									/>
								</Space>
							</Space>
						</Col>
					</Row>
				</>
				{contractDurations != null && (
					<div className={styles.selectedVoyages}>
						<Typography.Text>Selected contracts</Typography.Text>
						<Table
							size="small"
							pagination={false}
							dataSource={contractDurations}
							columns={[
								{
									title: 'Identifier',
									dataIndex: 'identifier',
									render: (c) => (<b>{c}</b>),
								},
								{
									title: 'Type',
									dataIndex: 'fixtureType',
									render: (c) => FixtureTypeLabels[c as keyof typeof FixtureTypeLabels],
								},
								{
									title: 'Start Date',
									dataIndex: 'startDate',
									render: (c, row) => (
										<div className={styles.dateBox}>
											<p className={styles.label}>{formatDate(c)}</p>
											<p className={styles.dateLabel}>{row.isStartDateEstimated ? 'estimated' : 'actual'}</p>
										</div>
									),
								},
								{
									title: 'End Date',
									dataIndex: 'endDate',
									render: (c, row) => (
										<div className={styles.dateBox}>
											<p className={styles.label}>{formatDate(c)}</p>
											<p className={styles.dateLabel}>{row.isEndDateEstimated ? 'estimated' : 'actual'}</p>
										</div>
									),
								},
								{
									title: 'Duration',
									dataIndex: 'duration',
									render: (c) => (<b>{c}</b>),
								},
							]}
						/>
					</div>
				)}
				<>
					<Divider />
					<Row>
						<Col span={6} className={styles.headerCol}>
							<Space size="middle">
								<Typography.Text>3.</Typography.Text>
								<Typography.Text>Items subject to close</Typography.Text>
							</Space>
						</Col>
					</Row>
					<br />
					<Card slim>
						<Table
							size="small"
							pagination={false}
							loading={loading}
							dataSource={eligibleItems}
							rowKey="id"
							columns={[
								{
									title: 'ID',
									dataIndex: 'identifier',
								},
								{
									title: 'Item',
									dataIndex: 'item',
								},
								{
									title: 'Vessel',
									dataIndex: ['vessel', 'name'],
								},
								{
									title: 'Contract',
									dataIndex: ['voyage', 'identifier'],
								},
								{
									title: 'Service From',
									dataIndex: 'dateFrom',
									render: (v) => formatDate(v, DATE_AND_TIME),
								},
								{
									title: 'Service To',
									dataIndex: 'dateTo',
									render: (v) => formatDate(v, DATE_AND_TIME),
								},
								{
									title: 'Invoice Date',
									dataIndex: 'invoiceDate',
									render: (v) => (v != null ? formatDate(v) : '-'),
								},
								{
									title: 'Total Amount',
									dataIndex: 'amount',
									render: (v, row) => formatCurrency(v, row.currency),
								},
								{
									title: 'Account Code',
									dataIndex: 'accountCode',
									render: (v) => v ?? <i>Unmapped</i>,
								},
							]}
						/>
					</Card>
				</>
				{previewItems != null && previewItems.length > 0 && (
					<>
						<Divider />
						<Row>
							<Col span={6} className={styles.headerCol}>
								<Space size="middle">
									<Typography.Text>4.</Typography.Text>
									<Typography.Text>Journal entries to be created</Typography.Text>
								</Space>
							</Col>
						</Row>
						<br />
						{hasUnmappedAccounts && (
							<Alert
								type="warning"
								showIcon
								message="Some items have unmapped account codes. Please configure the mapping before closing."
								style={{ marginBottom: 16 }}
							/>
						)}
						<Card slim>
							<Table
								size="small"
								pagination={false}
								dataSource={previewItems}
								rowKey={(_, idx) => `preview-${idx}`}
								columns={journalColumns}
							/>
						</Card>
					</>
				)}
				<>
					<Divider />
					<Row>
						<Space size="middle">
							<Button
								type="primary"
								onClick={onSubmit}
								disabled={
									eligibleItems.length === 0 ||
									closePeriod == null ||
									hasUnmappedAccounts ||
									isPeriodAlreadyClosedForSelection
								}
								confirmTitle="Are you sure you want to close this period? This action cannot be undone."
							>
								Close period
							</Button>
							<Button
								danger
								onClick={() => {
									setClosePeriod(null);
									setSelectedVessels([]);
									setSelectedVoyages([]);
									history.push(Links.Accounting.Accruals.get());
								}}
								confirmTitle="Are you sure you want to cancel? This will reset all fields."
							>
								Cancel
							</Button>
						</Space>
					</Row>
				</>
				{isPeriodAlreadyClosedForSelection && closePeriod != null && (
					<>
						<Divider />
						<Alert
							type="info"
							showIcon
							message="This period is already closed"
							description={`${closePeriod.format('YYYY-MM')} has already been closed for the selected vessel(s).`}
						/>
					</>
				)}
			</Card>
		</SimpleScreen>
	);
};

export default PeriodCloseScreen;
